import Subsection from "../Subsection";
import Image from "@Components/Image";
import config from "@Config/index";
import INSIGHTS_DATA from "./data/items";
import styles from "./bookYourPerfectAccommodation.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { useState } from "react";
import PropertyDetailsModal from "@Pages/PropertyPage/components/PropertyDetailsModal";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";

const BookYourPerfectAccommodation = ({
  isDesktop = false,
  customTitle,
  customSubtitle,
  hideLearnMore = false,
  customInsights,
}: {
  isDesktop?: boolean;
  customTitle?: string;
  customSubtitle?: string;
  hideLearnMore?: boolean;
  customInsights?: { title: string; subtitle: string }[];
}) => {
  const [showModal, setShowModal] = useState(false);
  const handleModalClose = () => setShowModal(false);
  const { t } = useTranslationHook();
  const { isAbove } = useResponsiveness();
  const isAboveMD = isAbove(SCREEN_SIZE.MD, false);
  const showDesktopInsight = isDesktop && isAboveMD;
  const title =
    customTitle || t("home.bookyourperfectaccommodation.bookyourperfectaccommodationtitle");
  const subtitle =
    customSubtitle || t("home.bookyourperfectaccommodation.bookyourperfectaccommodationsubtitle");
  const lowestPriceGuranteeS1 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section1",
  );
  const lowestPriceGuranteeS2 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section2",
  );
  const lowestPriceGuranteeS3 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section3.section3title",
  );
  const lowestPriceGuranteeS31 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section3.condition1",
  );
  const lowestPriceGuranteeS32 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section3.condition2",
  );
  const lowestPriceGuranteeS33 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section3.condition3",
  );
  const lowestPriceGuranteeS34 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section3.condition4",
  );
  const lowestPriceGuranteeS35 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section3.condition5",
  );
  const lowestPriceGuranteeS4 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section4.section4title",
  );
  const lowestPriceGuranteeS41 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section4.step1",
  );
  const lowestPriceGuranteeS42 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section4.step2",
  );
  const lowestPriceGuranteeS43 = t(
    "home.bookyourperfectaccommodation.lowestpriceguarantee.section4.step3",
  );
  // const actionButtonCTA = t("Okay, got it");
  // const modalHeaderTitle = t("Lowest Price Guarantee");

  return (
    <Subsection
      subtitleH4
      subSectionPaddingReduced={!showDesktopInsight}
      withSidePadding={!showDesktopInsight}
      title={title}
      subtitle={subtitle}
      isDesktop={showDesktopInsight}
    >
      <div
        className={styles.insightsContainer}
        data-testid="book-your-perfect-accomodation-section"
      >
        {INSIGHTS_DATA.map((data, idx) => (
          <div
            className={formatClassNames(
              styles.insight,
              idx === 3 ? styles.withGreenGradient : styles.withPinkGradient,
              showDesktopInsight && styles.desktopInsight,
            )}
            key={data.title}
            data-testid={data.title}
          >
            <Image
              src={
                config.IS_CHINA_APP
                  ? `${config.IMAGE_STATIC_URL}/images/${data.icon.replace("-insight", "")}.svg`
                  : `${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/${data.icon}.svg`
              }
              height={showDesktopInsight ? 46 : 32}
              width={showDesktopInsight ? 46 : 32}
              className={styles.icon}
            />
            <div className={styles.detailsContainer}>
              <span className={styles.title}>{customInsights?.[idx]?.title || t(data.title)}</span>
              <span className={styles.subtitle}>
                {customInsights?.[idx]?.subtitle || t(data.subtitle)}.{" "}
                {data.learnMore && !hideLearnMore && (
                  <span role="none" onClick={() => setShowModal(true)} className={styles.learnMore}>
                    {t("home.bookyourperfectaccommodation.lowestpriceguarantee.learnmore")}
                  </span>
                )}
              </span>
            </div>
          </div>
        ))}
        {showModal && (
          <PropertyDetailsModal
            actionButtonVariant="success"
            actionButtonText={t(
              "home.bookyourperfectaccommodation.lowestpriceguarantee.actionbuttoncta",
            )}
            header={t("home.bookyourperfectaccommodation.lowestpriceguarantee.modalheadertitle")}
            hidePropertyModal={handleModalClose}
            isDesktop={isDesktop}
          >
            <div className={styles.priceMatchGuaranteeModalContainer}>
              <div className={styles.pmgListWithoutIndent}>
                <li>{lowestPriceGuranteeS1}</li>
                <li>{lowestPriceGuranteeS2}</li>
              </div>
              <div className={styles.divWithMargin}>
                <div>{lowestPriceGuranteeS3}:</div>
                <ul>
                  <li>
                    <div>
                      <span>{lowestPriceGuranteeS31}</span>
                    </div>
                  </li>
                  <li>
                    <div>
                      <span>{lowestPriceGuranteeS32}</span>
                    </div>
                  </li>
                  <li>
                    <div>
                      <span>{lowestPriceGuranteeS33}</span>
                    </div>
                  </li>
                  <li>
                    <div>
                      <span>{lowestPriceGuranteeS34}</span>
                    </div>
                  </li>
                  <li>
                    <div>
                      <span>{lowestPriceGuranteeS35}</span>
                    </div>
                  </li>
                </ul>
              </div>
              <div className={styles.divWithMargin}>
                <div>{lowestPriceGuranteeS4}</div>
                <ul>
                  <li>
                    <div>
                      <span>{lowestPriceGuranteeS41}</span>
                    </div>
                  </li>
                  <li>
                    <div>
                      <span>{lowestPriceGuranteeS42}</span>
                    </div>
                  </li>
                  <li>
                    <div>
                      <span>{lowestPriceGuranteeS43}</span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </PropertyDetailsModal>
        )}
      </div>
    </Subsection>
  );
};

export default wrapperHOC(BookYourPerfectAccommodation, {
  componentName: "BookYourPerfectAccommodation-Home",
  showForChina: true,
  showForMiniProgram: false,
  showForApp: false,
});
